import type { TranslateService } from '@ngx-translate/core';
import type { WebWidgetModel, WidgetMetaData } from '@web/types';
import type { ButtonEntity, ButtonItem } from '@sites/entity-core';

import { createId } from '@paralleldrive/cuid2';

import { Widgets } from '@common/enums';
import { defaultWidgetParams, defaultWidgetPermission } from '@common/constants';
import { createButton } from '@sites/entity-core';

import { DEFAULT_BUTTON_ICON } from '@web/widgets/common/base/base-button/base-button.const';

import { ButtonControlComponent } from '../components/button-control/button-control.component';
import { ButtonViewComponent } from '../components/button-view/button-view.component';

// DEFAULT COMPONENT SETTINGS — sourced from the canonical entity factory in @sites/entity-core.
// The package owns the data shape but deliberately not the host-only concerns: the seeded
// button's localised label (i18n), its generated `elementId` (cuid2) and the default icon SVG
// (`buttonIconTemplates`) are passed in via `buttonOverrides` here.
//
// `data` is a function (not a static object) so `WebWidgetsFactoryService.prepareWidgetData`
// invokes it with `meta` — a button dropped into a zero block starts from the zero variant's
// defaults (`type: 'zero'` envelope tag, extended-position grid, fixed-width button).
const buttonData = (translate: TranslateService, meta?: WidgetMetaData): ButtonEntity['data'] => {
    const buttonOverrides: Partial<ButtonItem> = {
        elementId: createId(),
        buttonIcon: { ...DEFAULT_BUTTON_ICON },
    };

    // The overloads on `createButton` are picked per literal arg — calling each
    // branch separately keeps overload resolution happy.
    const entity =
        meta?.type === 'zero'
            ? createButton('zero', { buttonOverrides: { ...buttonOverrides, label: 'Visit' } })
            : createButton('default', {
                  buttonOverrides: { ...buttonOverrides, label: translate.instant('control_visit') },
              });

    return entity.data as ButtonEntity['data'];
};
// END DEFAULT SETTINGS

export const buttonWidgetModel: WebWidgetModel = {
    componentView: ButtonViewComponent,
    componentControl: ButtonControlComponent,
    name: Widgets.BUTTON,
    label: 'widget_button_label',
    data: buttonData,
    permission: defaultWidgetPermission,
    params: { ...defaultWidgetParams, type: ['default', 'specific', 'zero'] },
    signalForm: true,
};
